import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  getDoc,
  query,
  orderBy,
  doc,
  deleteDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase-config";

// Cloud function URL for Discord notifications
const DISCORD_WEBHOOK_FUNCTION_URL =
  "https://us-central1-just-quick-tools.cloudfunctions.net/externalAnjaliWebhook";

// ============================================
// MUSIC SUBSCRIBERS
// ============================================

export const addMusicSubscriber = async (email) => {
  try {
    if (!email || !email.includes("@")) {
      throw new Error("Invalid email address");
    }

    const docRef = await addDoc(collection(db, "musicSubs"), {
      email: email.toLowerCase().trim(),
      timestamp: serverTimestamp(),
      subscribed: true,
      source: "website",
    });

    console.log("Subscriber added with ID: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error adding subscriber: ", error);
    return { success: false, error: error.message };
  }
};

// ============================================
// CONTACT FORM
// ============================================

export const addContactMessage = async (formData) => {
  try {
    if (!formData.message || !formData.message.trim()) {
      throw new Error("Message is required");
    }

    if (formData.email && !formData.email.includes("@")) {
      throw new Error("Invalid email address");
    }

    const contactData = {
      name: formData.name ? formData.name.trim() : null,
      email: formData.email ? formData.email.toLowerCase().trim() : null,
      message: formData.message.trim(),
      timestamp: serverTimestamp(),
      source: "website",
      status: "unread",
    };

    const docRef = await addDoc(collection(db, "contacts"), contactData);

    // Send to Discord via cloud function (non-blocking)
    sendToDiscordWebhook({
      name: contactData.name,
      email: contactData.email,
      message: contactData.message,
    }).catch((error) => {
      console.warn("Discord notification failed:", error);
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error adding contact message: ", error);
    return { success: false, error: error.message };
  }
};

const sendToDiscordWebhook = async (contactData) => {
  try {
    const response = await fetch(DISCORD_WEBHOOK_FUNCTION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(contactData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("Discord notification sent:", result);
    return result;
  } catch (error) {
    console.error("Error sending to Discord webhook:", error);
    throw error;
  }
};

// ============================================
// MUSIC MANAGEMENT
// ============================================

/**
 * Upload a file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} path - The storage path (e.g., 'music/' or 'covers/')
 * @param {function} onProgress - Callback for upload progress (0-100)
 * @returns {Promise<string>} - Resolves with download URL
 */
export const uploadFile = async (file, path, onProgress) => {
  try {
    if (!file) throw new Error("No file provided");

    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `${path}${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) onProgress(Math.round(progress));
        },
        (error) => {
          console.error("Upload error:", error);
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        },
      );
    });
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const addSong = async (songData) => {
  try {
    if (!songData.title || !songData.title.trim())
      throw new Error("Song title is required");
    if (!songData.audioUrl) throw new Error("Audio file is required");
    if (!songData.coverUrl) throw new Error("Cover art is required");

    const song = {
      title: songData.title.trim(),
      artist: "Anjali Daswani",
      audioUrl: songData.audioUrl,
      coverUrl: songData.coverUrl,
      isLatest: songData.isLatest || false,
      streamingLinks: {
        spotify: songData.streamingLinks?.spotify || "",
        apple: songData.streamingLinks?.apple || "",
        amazon: songData.streamingLinks?.amazon || "",
        youtube: songData.streamingLinks?.youtube || "",
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, "songs"), song);
    console.log("Song added with ID: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error adding song: ", error);
    return { success: false, error: error.message };
  }
};

export const getAllSongs = async () => {
  try {
    const songsQuery = query(
      collection(db, "songs"),
      orderBy("createdAt", "desc"),
    );

    const querySnapshot = await getDocs(songsQuery);

    const songs = [];
    querySnapshot.forEach((doc) => {
      songs.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, songs };
  } catch (error) {
    console.error("Error getting songs: ", error);
    return { success: false, error: error.message, songs: [] };
  }
};

export const deleteSong = async (songId) => {
  try {
    if (!songId) throw new Error("Song ID is required");
    await deleteDoc(doc(db, "songs", songId));
    console.log("Song deleted with ID: ", songId);
    return { success: true };
  } catch (error) {
    console.error("Error deleting song: ", error);
    return { success: false, error: error.message };
  }
};

export const updateSong = async (songId, updates) => {
  try {
    if (!songId) throw new Error("Song ID is required");

    await updateDoc(doc(db, "songs", songId), {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    console.log("Song updated with ID: ", songId);
    return { success: true };
  } catch (error) {
    console.error("Error updating song: ", error);
    return { success: false, error: error.message };
  }
};

export const setLatestSong = async (songId) => {
  try {
    if (!songId) throw new Error("Song ID is required");

    const songsResult = await getAllSongs();
    if (!songsResult.success) throw new Error("Failed to fetch songs");

    const updatePromises = songsResult.songs.map((song) =>
      updateDoc(doc(db, "songs", song.id), {
        isLatest: song.id === songId,
        updatedAt: serverTimestamp(),
      }),
    );

    await Promise.all(updatePromises);
    console.log("Latest song set to ID: ", songId);
    return { success: true };
  } catch (error) {
    console.error("Error setting latest song: ", error);
    return { success: false, error: error.message };
  }
};

// ============================================
// CONTACT MESSAGES MANAGEMENT
// ============================================

export const getAllContactMessages = async () => {
  try {
    const messagesQuery = query(
      collection(db, "contacts"),
      orderBy("timestamp", "desc"),
    );

    const querySnapshot = await getDocs(messagesQuery);

    const messages = [];
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, messages };
  } catch (error) {
    console.error("Error getting contact messages: ", error);
    return { success: false, error: error.message, messages: [] };
  }
};

export const markMessageAsRead = async (messageId) => {
  try {
    if (!messageId) throw new Error("Message ID is required");

    await updateDoc(doc(db, "contacts", messageId), { status: "read" });

    console.log("Message marked as read: ", messageId);
    return { success: true };
  } catch (error) {
    console.error("Error marking message as read: ", error);
    return { success: false, error: error.message };
  }
};

export const deleteContactMessage = async (messageId) => {
  try {
    if (!messageId) throw new Error("Message ID is required");

    await deleteDoc(doc(db, "contacts", messageId));
    console.log("Message deleted: ", messageId);
    return { success: true };
  } catch (error) {
    console.error("Error deleting message: ", error);
    return { success: false, error: error.message };
  }
};

// ============================================
// TIKTOK VIDEOS MANAGEMENT
// ============================================

export const getAllTikTokVideos = async () => {
  try {
    const videosQuery = query(
      collection(db, "tiktokVideos"),
      orderBy("order", "asc"),
    );

    const querySnapshot = await getDocs(videosQuery);

    const videos = [];
    querySnapshot.forEach((doc) => {
      videos.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, videos };
  } catch (error) {
    console.error("Error getting TikTok videos: ", error);
    return { success: false, error: error.message, videos: [] };
  }
};

export const addTikTokVideo = async (videoData) => {
  try {
    if (!videoData.url || !videoData.url.trim())
      throw new Error("TikTok video URL is required");
    if (!videoData.embedHtml || !videoData.embedHtml.trim())
      throw new Error("TikTok embed HTML is required");
    if (!videoData.url.includes("tiktok.com"))
      throw new Error("Please provide a valid TikTok URL");
    if (!videoData.embedHtml.includes("tiktok-embed"))
      throw new Error("Invalid embed code. Please use TikTok's embed tool.");

    const titleMatch = videoData.embedHtml.match(/<p>(.*?)<\/p>/);
    const title = titleMatch
      ? titleMatch[1].replace(/<[^>]+>/g, "").substring(0, 100)
      : "";

    const video = {
      url: videoData.url.trim(),
      order: videoData.order || 1,
      isActive: videoData.isActive !== false,
      embedHtml: videoData.embedHtml.trim(),
      title: title || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, "tiktokVideos"), video);
    console.log("TikTok video added with ID: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error adding TikTok video: ", error);
    return { success: false, error: error.message };
  }
};

export const updateTikTokVideo = async (videoId, updates) => {
  try {
    if (!videoId) throw new Error("Video ID is required");

    await updateDoc(doc(db, "tiktokVideos", videoId), {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    console.log("TikTok video updated with ID: ", videoId);
    return { success: true };
  } catch (error) {
    console.error("Error updating TikTok video: ", error);
    return { success: false, error: error.message };
  }
};

export const deleteTikTokVideo = async (videoId) => {
  try {
    if (!videoId) throw new Error("Video ID is required");

    await deleteDoc(doc(db, "tiktokVideos", videoId));
    console.log("TikTok video deleted with ID: ", videoId);
    return { success: true };
  } catch (error) {
    console.error("Error deleting TikTok video: ", error);
    return { success: false, error: error.message };
  }
};

export const reorderTikTokVideos = async (videos) => {
  try {
    if (!videos || videos.length === 0)
      throw new Error("Videos array is required");

    const updatePromises = videos.map((video, index) =>
      updateDoc(doc(db, "tiktokVideos", video.id), {
        order: index + 1,
        updatedAt: serverTimestamp(),
      }),
    );

    await Promise.all(updatePromises);
    console.log("TikTok videos reordered successfully");
    return { success: true };
  } catch (error) {
    console.error("Error reordering TikTok videos: ", error);
    return { success: false, error: error.message };
  }
};

// ============================================
// BIO MANAGEMENT
// ============================================

const BIO_DOC_ID = "Ro8KvrgtD5MORJwwlPfB";

// Module-level cache — persists for the lifetime of the app session.
let _bioCache = null;

export const getBioContent = async () => {
  if (_bioCache) return { success: true, bio: _bioCache };

  try {
    const bioRef = doc(db, "bio", BIO_DOC_ID);
    const bioSnap = await getDoc(bioRef);

    if (bioSnap.exists()) {
      _bioCache = bioSnap.data();
      return { success: true, bio: _bioCache };
    } else {
      return { success: false, error: "Bio document not found" };
    }
  } catch (error) {
    console.error("Error getting bio content:", error);
    return { success: false, error: error.message };
  }
};

export const updateBioContent = async (bioData) => {
  try {
    const bioRef = doc(db, "bio", BIO_DOC_ID);

    await setDoc(
      bioRef,
      { ...bioData, updatedAt: serverTimestamp() },
      { merge: true },
    );

    _bioCache = null;
    console.log("Bio content updated");
    return { success: true };
  } catch (error) {
    console.error("Error updating bio content:", error);
    return { success: false, error: error.message };
  }
};

// ============================================
// HOME PAGE CONFIG
// Stored at: content/homeConfig
//
// Shape:
//   heroImageUrl         {string}  — hero section photo URL
//   featuredTitle        {string}  — song title in the banner
//   featuredDescription  {string}  — subtitle under the title
//   featuredSpotifyUrl   {string}  — Spotify link for the button
//   featuredImageUrl     {string}  — banner background image URL
//   featuredBgPositionY  {number}  — 0–100, background-position Y %
// ============================================

// Module-level cache — same pattern as _bioCache
let _homeConfigCache = null;

const HOME_CONFIG_DEFAULTS = {
  heroImageUrl:
    "https://firebasestorage.googleapis.com/v0/b/anjalidmusic.firebasestorage.app/o/images%2FAnjaliShoot_042024-6.jpg?alt=media&token=323671f6-48a2-4e4f-82bd-40165df8a967",
  featuredTitle: "Girl on the Train",
  featuredDescription:
    "My latest single - a story of connection, dreams, and the moments that change everything.",
  featuredSpotifyUrl: "https://open.spotify.com/track/7rJiLV0jXqcLesKQSvCniD",
  featuredImageUrl:
    "https://firebasestorage.googleapis.com/v0/b/anjalidmusic.firebasestorage.app/o/images%2Flarge%20rectangle%20pictures%20near%20train.jpeg?alt=media&token=f896e4f6-3d22-4334-8f95-3849bf29890b",
  featuredBgPositionY: 0,
};

/**
 * Get home page config from Firestore (cached per session).
 * Falls back to hardcoded defaults if the document doesn't exist yet.
 * @returns {Promise<{success: boolean, config: object}>}
 */
export const getHomeConfig = async () => {
  if (_homeConfigCache) {
    return { success: true, config: _homeConfigCache };
  }

  try {
    const snap = await getDoc(doc(db, "content", "homeConfig"));

    const config = snap.exists()
      ? { ...HOME_CONFIG_DEFAULTS, ...snap.data() }
      : { ...HOME_CONFIG_DEFAULTS };

    _homeConfigCache = config;
    return { success: true, config };
  } catch (error) {
    console.error("Error fetching home config:", error);
    return {
      success: false,
      error: error.message,
      config: { ...HOME_CONFIG_DEFAULTS },
    };
  }
};

/**
 * Update home page config in Firestore and invalidate cache.
 * @param {Object} fields - Partial or full homeConfig fields to merge
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateHomeConfig = async (fields) => {
  try {
    await setDoc(
      doc(db, "content", "homeConfig"),
      { ...fields, updatedAt: serverTimestamp() },
      { merge: true },
    );

    _homeConfigCache = null;
    console.log("Home config updated");
    return { success: true };
  } catch (error) {
    console.error("Error updating home config:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Upload an image to covers/ in Firebase Storage.
 * The filename is: covers/{filenamePrefix}-{timestamp}.{ext}
 * @param {File} file - Image file (JPG / PNG / WebP)
 * @param {string} filenamePrefix - e.g. "hero-photo" or "featured-bg"
 * @param {function} onProgress - optional progress callback (0–100)
 * @returns {Promise<string>} - Resolves with the download URL
 */
export const uploadImage = (file, filenamePrefix, onProgress) => {
  return new Promise((resolve, reject) => {
    const ext = file.name.split(".").pop();
    const filename = `${filenamePrefix}-${Date.now()}.${ext}`;
    const storageRef = ref(storage, `covers/${filename}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const pct = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        if (onProgress) onProgress(pct);
      },
      (error) => {
        console.error("Image upload error:", error);
        reject(error);
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        } catch (err) {
          reject(err);
        }
      },
    );
  });
};
